import React, { useState, useMemo, useEffect } from 'react'
import { Popover, Tooltip, Typography } from 'antd'
import { Icon } from '@iconify/react'
import AssigneeSelector from './AssigneeSelector'

const { Text } = Typography

const AssigneeSelectorButton = ({
    selectedAssignee,
    onSelect,
    projectId,
    filteredAssignees,
    getAssigneeColor,
    getInitials,
    parentComponent,
    showName = false,
    taskListKind = 'support',
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    onTabNext,
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const isControlled = controlledOpen !== undefined
    const assigneePopoverOpen = isControlled
        ? Boolean(controlledOpen)
        : uncontrolledOpen

    const setAssigneePopoverOpen = (nextOpen) => {
        if (!isControlled) setUncontrolledOpen(nextOpen)
        controlledOnOpenChange?.(nextOpen)
    }

    // Helper functions if not provided
    const getInitialsFn =
        getInitials ||
        ((name) => {
            if (!name) return 'U'
            const parts = name.split(' ')
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase()
            }
            return name.substring(0, 2).toUpperCase()
        })

    const getAssigneeColorFn =
        getAssigneeColor ||
        ((name) => {
            if (!name) return '#e2e8f0'
            const colors = [
                '#85BBF9',
                '#6366f1',
                '#8b5cf6',
                '#ec4899',
                '#f59e0b',
                '#10b981',
                '#06b6d4',
            ]
            const index = name.charCodeAt(0) % colors.length
            return colors[index]
        })

    const selectedAssigneeData = useMemo(() => {
        if (!selectedAssignee) return null
        // Object from AssigneeSelector / Assign to Me / task.assigneeId[0]
        if (
            typeof selectedAssignee === 'object' &&
            (selectedAssignee.name ||
                selectedAssignee._id ||
                selectedAssignee.id)
        ) {
            return selectedAssignee
        }
        // Otherwise find in filteredAssignees
        if (filteredAssignees && Array.isArray(filteredAssignees)) {
            return filteredAssignees.find(
                (data) => data?.value === selectedAssignee
            )
        }
        return null
    }, [filteredAssignees, selectedAssignee])

    const handleAssigneeChange = (selected) => {
        onSelect?.(selected)
        setAssigneePopoverOpen(false)
    }

    const assigneeName = useMemo(() => {
        if (selectedAssigneeData) {
            return (
                selectedAssigneeData.name ||
                selectedAssigneeData.label ||
                selectedAssigneeData.email ||
                null
            )
        }
        // For ProjectTaskList, selectedAssignee might be task.assigneeId[0]
        if (selectedAssignee && typeof selectedAssignee === 'object') {
            return (
                selectedAssignee.name ||
                selectedAssignee.label ||
                selectedAssignee.email ||
                null
            )
        }
        return null
    }, [selectedAssigneeData, selectedAssignee])

    const hasAssignee = Boolean(
        selectedAssigneeData ||
            (selectedAssignee &&
                typeof selectedAssignee === 'object' &&
                (selectedAssignee.name ||
                    selectedAssignee._id ||
                    selectedAssignee.id))
    )

    const profilePicture = useMemo(() => {
        if (selectedAssigneeData?.profilePicture) {
            return selectedAssigneeData.profilePicture
        }
        if (
            selectedAssignee &&
            typeof selectedAssignee === 'object' &&
            selectedAssignee.profilePicture
        ) {
            return selectedAssignee.profilePicture
        }
        return null
    }, [selectedAssigneeData, selectedAssignee])

    const [imageError, setImageError] = useState(false)

    // Reset image error when profilePicture changes
    useEffect(() => {
        setImageError(false)
    }, [profilePicture])

    return (
        <Popover
            content={
                <AssigneeSelector
                    onSelect={handleAssigneeChange}
                    onClose={() => setAssigneePopoverOpen(false)}
                    selectedAssignee={selectedAssigneeData}
                    projectId={projectId}
                    taskListKind={taskListKind}
                    open={assigneePopoverOpen}
                    onTabNext={onTabNext}
                />
            }
            trigger={onSelect ? 'click' : []}
            placement="bottomLeft"
            open={onSelect ? assigneePopoverOpen : false}
            onOpenChange={(open) => {
                if (!onSelect) return
                setAssigneePopoverOpen(open)
            }}
            arrow={false}
            styles={{
                body: {
                    padding: 0,
                },
                content: {
                    width: '400px',
                },
            }}
            getPopupContainer={() => document.body}
        >
            {showName ? (
                <div
                    onClick={(e) => {
                        e.stopPropagation()
                        if (!onSelect) return
                        setAssigneePopoverOpen(true)
                    }}
                    style={{
                        cursor: onSelect ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width:
                                parentComponent === 'taskDetailView'
                                    ? '28px'
                                    : '24px',
                            height:
                                parentComponent === 'taskDetailView'
                                    ? '28px'
                                    : '24px',
                            borderRadius: '50%',
                            backgroundColor: hasAssignee
                                ? getAssigneeColorFn(assigneeName)
                                : 'transparent',
                            color: hasAssignee ? '#ffffff' : '#475569',
                            fontSize: '11px',
                            fontWeight: 600,
                            border: hasAssignee ? 'none' : '1px dashed #cbd5e1',
                            flexShrink: 0,
                        }}
                    >
                        {hasAssignee ? (
                            profilePicture && !imageError ? (
                                <img
                                    src={profilePicture}
                                    alt={assigneeName || 'Assignee'}
                                    style={{
                                        width:
                                            parentComponent === 'taskDetailView'
                                                ? '28px'
                                                : '24px',
                                        height:
                                            parentComponent === 'taskDetailView'
                                                ? '28px'
                                                : '24px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                    }}
                                    onError={() => {
                                        setImageError(true)
                                    }}
                                />
                            ) : (
                                <Text
                                    style={{
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                    }}
                                >
                                    {getInitialsFn(assigneeName)}
                                </Text>
                            )
                        ) : (
                            <Icon
                                icon="mi:user"
                                width={16}
                                height={16}
                                style={{ color: '#94a3b8' }}
                            />
                        )}
                    </div>
                    {hasAssignee && assigneeName && (
                        <Text
                            style={{
                                fontSize: '14px',
                                color: '#1e293b',
                                fontWeight: 400,
                            }}
                        >
                            {assigneeName}
                        </Text>
                    )}
                </div>
            ) : (
                <Tooltip
                    title={hasAssignee ? assigneeName : 'Assign this task'}
                    placement="top"
                >
                    <div
                        onClick={(e) => {
                            e.stopPropagation()
                            if (!onSelect) return
                            setAssigneePopoverOpen(true)
                        }}
                        style={{
                            cursor: onSelect ? 'pointer' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width:
                                parentComponent === 'taskDetailView'
                                    ? '28px'
                                    : '24px',
                            height:
                                parentComponent === 'taskDetailView'
                                    ? '28px'
                                    : '24px',
                            borderRadius: '50%',
                            backgroundColor: hasAssignee
                                ? getAssigneeColorFn(assigneeName)
                                : 'transparent',
                            color: hasAssignee ? '#ffffff' : '#475569',
                            fontSize: '11px',
                            fontWeight: 600,
                            border: hasAssignee ? 'none' : '1px dashed #cbd5e1',
                        }}
                    >
                        {hasAssignee ? (
                            profilePicture && !imageError ? (
                                <img
                                    src={profilePicture}
                                    alt={assigneeName || 'Assignee'}
                                    style={{
                                        width:
                                            parentComponent === 'taskDetailView'
                                                ? '28px'
                                                : '24px',
                                        height:
                                            parentComponent === 'taskDetailView'
                                                ? '28px'
                                                : '24px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                    }}
                                    onError={() => {
                                        setImageError(true)
                                    }}
                                />
                            ) : (
                                <Text
                                    style={{
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                    }}
                                >
                                    {getInitialsFn(assigneeName)}
                                </Text>
                            )
                        ) : (
                            <Icon
                                icon="mi:user"
                                width={16}
                                height={16}
                                style={{ color: '#94a3b8' }}
                            />
                        )}
                    </div>
                </Tooltip>
            )}
        </Popover>
    )
}

export default AssigneeSelectorButton
